import { FC, JSX, useEffect, useState } from "react";
import { ColumnConfig, SortDirections, Waffles, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { SortingMetadata } from "@app/shared/waffles/exports";
import { ProviderDetailSimpleModel } from "./providerSet";
import ProviderSetService from "./providerSetService";
import { ProviderSetVersionSummaryType, ProviderSetDataSortColumn } from "./enumerations";
import moment from "moment";

interface ProviderDetailsTableData {
  [wafflesKeyColumnName]: string;
  address: JSX.Element;
  dateDeactivated: string;
  name: string;
  npi: JSX.Element;
  phone: string;
  specCode: string;
}

interface PageSettings {
  skip: number,
  sortDetails: SortingMetadata<ProviderDetailsTableData>,
}

interface ProviderDetailsGridProps {
  providerSetId?: string;
  versionId?: string;
  summaryType: ProviderSetVersionSummaryType;
  filter?: string;
  pageSettings?: PageSettings;
  take: number;
  totalRecords?: number;
  onUpdate: (response: ProviderDetailsGridOnUpdateResponse) => void;
}

interface ProviderDetailsGridOnUpdateResponse {
  hasRecords: boolean;
  providerSetName: string;
  pageSettings: PageSettings;
}

const ProviderDetailsGrid: FC<React.PropsWithChildren<ProviderDetailsGridProps>> = (props: ProviderDetailsGridProps) => {

  const providerSetService = new ProviderSetService();

  const [loading, setLoading] = useState(false);
  const [providerDetails, setProviderDetails] = useState<ProviderDetailsTableData[]>([]);
  const [providerDetailsColumnConfig, setProviderDetailsColumnConfig] = useState<ColumnConfig>({});
  const [take] = useState(props.take);
  const [totalRecords, setTotalRecords] = useState(0);
  const [numDispRecLowerBound, setNumDispRecLowerBound] = useState(0);
  const [numDispRecUpperBound, setNumDispRecUpperBound] = useState(0);
  const [pageSettings, setPageSettings] = useState<PageSettings>(!!props.pageSettings ? props.pageSettings :
    {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.Asc,
        sortedColumn: ProviderSetDataSortColumn.NPI,
      },
    });

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => {
      setPageSettings({...pageSettings,skip: (pageNumber - 1) * take});
      window.scrollTo(0, 0);
    },
    recordsPerPage: take,
    skip: pageSettings.skip,
    totalRecords: totalRecords,
  }

  useEffect(() => {
    fetchProviderDetails();
  }, [pageSettings, props.versionId]);

  const handleSort = (data: ProviderDetailsTableData[], newSortColumn: keyof ProviderDetailsTableData, direction: SortDirections) => {
    setPageSettings({
      ...pageSettings,
      skip:0,
      sortDetails:{
        sortDirection: direction,
        sortedColumn: newSortColumn,
      }
    });
    return data;
  }

  const fetchProviderDetails = async () => {
    setLoading(true);

    let summaryType = props.summaryType;
    if (!summaryType)
      summaryType = ProviderSetVersionSummaryType.NoFilter

    const providers = await providerSetService.getProviderSetVersionDetail(props.providerSetId, props.versionId, summaryType, props.filter, pageSettings.skip, take, pageSettings.sortDetails.sortedColumn as ProviderSetDataSortColumn, pageSettings.sortDetails.sortDirection);

    const providerSet = await providerSetService.getProviderSet(props.providerSetId);

    if (!!props.totalRecords) {
      setTotalRecords(props.totalRecords);
    } else if (summaryType === ProviderSetVersionSummaryType.NoFilter) {
      const overallSummary = await providerSetService.getProviderSetVersionOverallSummary(props.providerSetId, props.versionId);
      setTotalRecords(overallSummary.totalAccessPoints);
    } else {
      const summaryByType = await providerSetService.getProviderSetVersionSummaryCount(props.providerSetId, props.versionId, summaryType);
      const summary = summaryByType.summaryCount.find(x => x.key.toUpperCase() === props.filter.toUpperCase())
      setTotalRecords(!!summary ? summary.totalRecordCount : 0);
    }

    setProviderDetailsColumnConfig(createWafflesConfig(providers.some(x => !!x.dateDeactivated)));
    setProviderDetails(createWafflesTable(providers));
    setNumDispRecLowerBound(pageSettings.skip + 1);
    setNumDispRecUpperBound(providers.length + pageSettings.skip);

    props.onUpdate({
      hasRecords: !!providers && providers.length > 0,
      pageSettings: pageSettings,
      providerSetName: providerSet.resource.name,
    })

    setLoading(false);
  };

  const createWafflesTable = (data: ProviderDetailSimpleModel[]) => {
    return data.map((rec) => {
      const npi = (
        <div>
          {rec.npi}
        </div>
      );

      const address = (
        <div>
          {rec.address}
          {!!rec.address2 ? `, ${rec.address2}`: ""}<br/>
          {`${rec.city}, ${rec.state} ${rec.zip}`}
        </div>);

      /* eslint-disable sort-keys */
      return ({
        key: rec.providerId.toString(),
        npi: npi,
        dateDeactivated: (!rec.dateDeactivated ? "" : moment.utc(rec.dateDeactivated).format("YYYY-MM-DD")),
        name: rec.name,
        address: address,
        phone: rec.phone,
        specCode: (!!rec.specialtyCode ? rec.specialtyCode : "<Not Provided>")
      });
      /* eslint-enable sort-keys */
    });
  };

  const createWafflesConfig = (showDeactivatedDate: boolean): ColumnConfig => {
    return  {
      address: {
        displayText: "Address",
        testId: "address",
        wrapContent: true,
      },
      dateDeactivated: {
        displayText: "Date Deactivated",
        hidden: !showDeactivatedDate,
        testId: "dateDeactivated",
      },
      name: {
        displayText: "Name",
        testId: "name",
      },
      npi: {
        displayText: "NPI",
        testId: "npi",
      },
      phone: {
        displayText: "Phone",
        noSort: true,
        testId: "phone",
      },
      specCode: {
        displayText: "Specialty Code",
        testId: "specCode",
      },
    };
  }

  return (
    <>
      {(!!providerDetails && providerDetails.length > 0) || loading ?
        <>
          {!loading ?
            (<p>Displaying {totalRecords > 0 ? `${numDispRecLowerBound.toLocaleString("en-US")}-${numDispRecUpperBound.toLocaleString("en-US")} of ${totalRecords.toLocaleString("en-US")}` : "0"} providers</p>)
            :null
          }
          <div className="provider-details-grid">
            <Waffles<ProviderDetailsTableData>
              columns={providerDetailsColumnConfig}
              customSort={handleSort}
              data={providerDetails}
              enablePagination={true}
              fullWidth={true}
              loading={loading}
              passThroughSorting={true}
              paginationProps={paginationProps}
              testId="provider-details-grid-waffles-table"
            />
          </div>
        </> : <p className="lead">There are no providers.</p>
      }
    </>
  );
}

export {
  PageSettings,
  ProviderDetailsGrid,
  ProviderDetailsGridOnUpdateResponse
}